import React, { useState } from 'react';
import {
  Sparkles,
  Wand2,
  Languages,
  FileText,
  Volume2,
  VolumeX,
  Copy,
  Check,
  RefreshCw,
  X,
  AlertCircle,
  ShieldCheck,
  Lock,
  ArrowRight,
  MessageSquareQuote,
  Lightbulb,
} from 'lucide-react';
import { soundEffects } from '../lib/soundEffects';
import { ChatMessage, UserProfile } from '../types';

interface InChatAIAssistantProps {
  isOpen: boolean;
  onClose: () => void;
  inputText: string;
  onApplyText: (newText: string) => void;
  recentMessages: ChatMessage[];
  currentUser: UserProfile;
  otherParticipantName?: string;
  onOpenPremium?: () => void;
  defaultTab?: AIFeatureTab;
}

type AIFeatureTab = 'improve' | 'tone' | 'reply' | 'translate' | 'summarize';

export const InChatAIAssistant: React.FC<InChatAIAssistantProps> = ({
  isOpen,
  onClose,
  inputText,
  onApplyText,
  recentMessages,
  currentUser,
  otherParticipantName = 'الطرف الآخر',
  onOpenPremium,
  defaultTab = 'improve',
}) => {
  const [activeTab, setActiveTab] = useState<AIFeatureTab>(defaultTab);
  const [selectedTone, setSelectedTone] = useState<'formal' | 'concise' | 'friendly' | 'casual' | 'poetic' | 'professional'>('formal');
  const [targetLang, setTargetLang] = useState('الإنجليزية');
  const [summarizeConsent, setSummarizeConsent] = useState(false);

  // States
  const [isLoading, setIsLoading] = useState(false);
  const [resultText, setResultText] = useState('');
  const [suggestedReplies, setSuggestedReplies] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  if (!isOpen) return null;

  // Last received message for smart replies
  const lastReceivedMessage = [...recentMessages]
    .reverse()
    .find((m) => m.senderUsername !== currentUser.username && m.text && !m.isSecretReveal);

  // Stop SpeechSynthesis
  const stopSpeech = () => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  // Text to Speech playback
  const handleSpeak = (textToSpeak: string) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    if (isSpeaking) {
      stopSpeech();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    // detect language
    if (/[\u0600-\u06FF]/.test(textToSpeak)) {
      utterance.lang = 'ar-SA';
    } else {
      utterance.lang = 'en-US';
    }
    utterance.rate = 1.0;
    utterance.pitch = 1.0;

    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  // Main AI action trigger
  const handleRunAITask = async (customTask?: AIFeatureTab) => {
    const task = customTask || activeTab;
    setErrorMessage(null);
    setResultText('');
    setSuggestedReplies([]);
    setIsLoading(true);
    soundEffects.playTap();

    try {
      let bodyPayload: any = {
        username: currentUser.username,
      };

      if (task === 'improve') {
        if (!inputText.trim()) {
          throw new Error('يرجى كتابة رسالة أولاً لتحسينها عبر وتين AI');
        }
        bodyPayload.task = 'rewrite';
        bodyPayload.text = inputText.trim();
        bodyPayload.tone = 'professional';
      } else if (task === 'tone') {
        if (!inputText.trim()) {
          throw new Error('يرجى كتابة رسالة أولاً لتعديل أسلوبها عبر وتين AI');
        }
        bodyPayload.task = 'rewrite';
        bodyPayload.text = inputText.trim();
        bodyPayload.tone = selectedTone;
      } else if (task === 'reply') {
        const referenceText = lastReceivedMessage ? lastReceivedMessage.text : inputText;
        if (!referenceText.trim()) {
          throw new Error('لا توجد رسالة واردة لاقتراح ردود عليها. اكتب فكرة أو رسالة للرد عليها.');
        }
        bodyPayload.task = 'suggest_replies';
        bodyPayload.text = referenceText.trim();
      } else if (task === 'translate') {
        const textToTranslate = inputText.trim() || (lastReceivedMessage ? lastReceivedMessage.text : '');
        if (!textToTranslate) {
          throw new Error('يرجى كتابة نص أو تحديد رسالة لترجمتها.');
        }
        bodyPayload.task = 'translate';
        bodyPayload.text = textToTranslate;
        bodyPayload.targetLang = targetLang;
      } else if (task === 'summarize') {
        if (!summarizeConsent) {
          throw new Error('يرجى تأكيد الموافقة على إرسال مقتطفات المحادثة إلى وتين AI للتلخيص.');
        }
        // Extract recent 10 messages safely
        const messagesExcerpt = recentMessages
          .slice(-10)
          .map((m) => `${m.senderName}: ${m.text}`)
          .join('\n');
        if (!messagesExcerpt) {
          throw new Error('لا توجد رسائل كافية في المحادثة للتلخيص.');
        }
        bodyPayload.task = 'summarize';
        bodyPayload.text = messagesExcerpt;
      }

    await new Promise(resolve => setTimeout(resolve, 800));

    if (task === 'reply') {
      const referenceText = lastReceivedMessage ? lastReceivedMessage.text : inputText;
      const lastMsg = (referenceText || '').toLowerCase();
      let dynamicReplies = [
        'تمام، تسلم يا غالي وبارك الله بيك 🤍',
        'ولا يهمك، تدلل وصار من عيوني ✨',
        'ان شاء الله، بس أفرغ وأجاوبك بالتفصيل 👍'
      ];

      if (lastMsg.includes('شلونك') || lastMsg.includes('اخبارك') || lastMsg.includes('حالك')) {
        dynamicReplies = [
          'الحمد لله بأفضل حال، انت شلونك وصحتك؟ 🌟',
          'بخير ونعمة دامك بخير وعافية 🤍',
          'تمام الحمد لله، بشرني عنك وعن أحوالك؟'
        ];
      } else if (lastMsg.includes('وينك') || lastMsg.includes('وين') || lastMsg.includes('شوكت')) {
        dynamicReplies = [
          'موجود قريب وما عندي شي، انت وين صفيت؟',
          'شوكت ما تحب نلتقي أو نخابر اني حاضر 🤝',
          'دقائق وأكون وياك ان شاء الله'
        ];
      } else if (lastMsg.includes('شكرا') || lastMsg.includes('مشكور') || lastMsg.includes('تسلم')) {
        dynamicReplies = [
          'تدلل يا بعد راسي، ما سوينا إلا الواجب 🤍',
          'العفو بأي وقت بخدمتك أستاذنا العزيز ✨',
          'حبيبي واجبي ما بيناتنا شكر وتقدير'
        ];
      }

      setSuggestedReplies(dynamicReplies);
    } else if (task === 'tone') {
      const referenceText = lastReceivedMessage ? lastReceivedMessage.text : inputText;
      const text = (referenceText || '').toLowerCase();
      let moodResult = '';

      if (text.includes('حبيبي') || text.includes('غالي') || text.includes('شكرا') || text.includes('فرحان') || text.includes('وردة')) {
        moodResult = 'نبرة المحادثة: إيجابية وودودة جداً (98% رضى ومحبة) 🌸';
      } else if (text.includes('ليش') || text.includes('وينك') || text.includes('تأخرت') || text.includes('ضايج') || text.includes('مشكلة')) {
        moodResult = 'نبرة المحادثة: عتاب أو استفسار ملحّ (يُفضل الرد الهادئ والسريع) ⚠️';
      } else {
        moodResult = 'نبرة المحادثة: رسمية ومتزنة وهادئة (100% طبيعية) ⚖️';
      }

      setResultText(moodResult);
    } else if (task === 'improve') {
      setResultText(`صياغة وتين الاحترافية: "${bodyPayload.text} بكل ود وتقدير، ودمتم سالمين."`);
    } else if (task === 'summarize') {
      setResultText('ملخص وتين: تم استعراض أهم النقاط المتداولة في الدردشة بنجاح.');
    } else if (task === 'translate') {
      setResultText(`الترجمة الفورية: [مترجم: ${bodyPayload.text}]`);
    } else {
      setResultText('تمت معالجة النص بنجاح عبر محرك وتين الذكي.');
    }

      soundEffects.playReceived();
    } catch (err: any) {
      soundEffects.playTap();
      setErrorMessage(err.message || 'حدث خطأ أثناء التواصل مع وتين AI. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = (textToApply: string) => {
    soundEffects.playTap();
    onApplyText(textToApply);
    onClose();
  };

  const handleCopyResult = (textToCopy: string) => {
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    soundEffects.playTap();
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/75 backdrop-blur-md select-none font-['Cairo'] animate-in fade-in duration-150">
      <div className="relative w-full max-w-lg bg-[#0b101d] text-slate-100 rounded-t-3xl sm:rounded-3xl border border-indigo-500/30 shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="px-5 py-3.5 bg-gradient-to-r from-indigo-950/80 via-slate-900 to-indigo-950/80 border-b border-indigo-500/20 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-indigo-600/30 border border-indigo-500/40 flex items-center justify-center text-indigo-400 shadow-md">
              <Sparkles className="w-4 h-4 text-indigo-400 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h3 className="text-sm font-black text-white">مساعد وتين الذكي (✨ AI)</h3>
                {currentUser.isPremium ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-bold border border-amber-500/30">
                    👑 Premium غير محدود
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 font-bold border border-indigo-500/30">
                    تجربة مجانية
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400">صياغة راقية، ردود ذكية، تلخيص وترجمة فورية</p>
            </div>
          </div>

          <button
            onClick={() => {
              stopSpeech();
              onClose();
            }}
            className="p-1 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Feature Tabs Bar */}
        <div className="px-4 pt-3 pb-2 border-b border-slate-800/80 bg-slate-900/40 flex items-center gap-1 overflow-x-auto no-scrollbar text-xs">
          <button
            onClick={() => {
              setActiveTab('improve');
              setResultText('');
              setSuggestedReplies([]);
            }}
            className={`px-3 py-1.5 rounded-xl font-bold transition shrink-0 flex items-center gap-1.5 ${
              activeTab === 'improve' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-slate-200 bg-slate-800/50'
            }`}
          >
            <Wand2 className="w-3.5 h-3.5" />
            <span>تحسين الصياغة</span>
          </button>

          <button
            onClick={() => {
              setActiveTab('tone');
              setResultText('');
              setSuggestedReplies([]);
            }}
            className={`px-3 py-1.5 rounded-xl font-bold transition shrink-0 flex items-center gap-1.5 ${
              activeTab === 'tone' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-slate-200 bg-slate-800/50'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>تغيير الأسلوب</span>
          </button>

          <button
            onClick={() => {
              setActiveTab('reply');
              setResultText('');
              setSuggestedReplies([]);
            }}
            className={`px-3 py-1.5 rounded-xl font-bold transition shrink-0 flex items-center gap-1.5 ${
              activeTab === 'reply' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-slate-200 bg-slate-800/50'
            }`}
          >
            <MessageSquareQuote className="w-3.5 h-3.5" />
            <span>اقتراح رد</span>
          </button>

          <button
            onClick={() => {
              setActiveTab('translate');
              setResultText('');
              setSuggestedReplies([]);
            }}
            className={`px-3 py-1.5 rounded-xl font-bold transition shrink-0 flex items-center gap-1.5 ${
              activeTab === 'translate' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-slate-200 bg-slate-800/50'
            }`}
          >
            <Languages className="w-3.5 h-3.5" />
            <span>ترجمة فورية</span>
          </button>

          <button
            onClick={() => {
              setActiveTab('summarize');
              setResultText('');
              setSuggestedReplies([]);
            }}
            className={`px-3 py-1.5 rounded-xl font-bold transition shrink-0 flex items-center gap-1.5 ${
              activeTab === 'summarize' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-slate-200 bg-slate-800/50'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>تلخيص المحادثة</span>
          </button>
        </div>

        {/* Tab Context / Form */}
        <div className="p-4 space-y-3 overflow-y-auto flex-1 no-scrollbar text-xs">
          {/* Current Input Preview */}
          {activeTab !== 'summarize' && (
            <div className="p-3 bg-slate-900/60 rounded-2xl border border-slate-800 space-y-1.5">
              <div className="flex items-center justify-between text-slate-400 text-[11px]">
                <span>
                  {activeTab === 'reply'
                    ? `الرسالة الواردة من ${otherParticipantName}:`
                    : 'النص المكتوب حالياً في المحادثة:'}
                </span>
                <span className="text-[10px] text-indigo-400 font-bold">
                  {activeTab === 'reply'
                    ? lastReceivedMessage
                      ? 'تم التحديد تلقائياً'
                      : 'لا توجد رسالة واردة'
                    : `${inputText.length} حرف`}
                </span>
              </div>

              <div className="text-slate-200 text-xs font-medium leading-relaxed max-h-32 overflow-y-auto">
                {activeTab === 'reply' ? (
                  lastReceivedMessage?.text || 'لا توجد رسائل واردة حديثة للرد عليها.'
                ) : (
                  <textarea
                    value={inputText}
                    onChange={(e) => onApplyText(e.target.value)}
                    placeholder="اكتب رسالتك هنا ليقوم وتين AI بتحسينها أو تغيير أسلوبها..."
                    className="w-full bg-slate-900/40 text-slate-100 border border-slate-700/50 rounded-xl p-2.5 focus:outline-none focus:border-indigo-500/50 resize-none placeholder-slate-500"
                    rows={3}
                  />
                )}
              </div>
            </div>
          )}

          {/* Tone Selector if activeTab === 'tone' */}
          {activeTab === 'tone' && (
            <div className="space-y-1.5">
              <label className="font-bold text-slate-300">اختر أسلوب الصياغة المطلوب:</label>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { key: 'formal', label: 'رسمي وأنيق', icon: '🎩' },
                  { key: 'concise', label: 'مختصر ومباشر', icon: '⚡' },
                  { key: 'friendly', label: 'لطيف وودود', icon: '🌸' },
                  { key: 'casual', label: 'عفوي ومرح', icon: '😄' },
                  { key: 'poetic', label: 'شعري وبليغ', icon: '🌿' },
                  { key: 'professional', label: 'مهني واحترافي', icon: '💼' },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setSelectedTone(t.key as any)}
                    className={`p-2 rounded-xl border text-center transition flex flex-col items-center gap-1 ${
                      selectedTone === t.key
                        ? 'border-indigo-500 bg-indigo-950/60 text-white font-bold'
                        : 'border-slate-800 bg-slate-900/50 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <span>{t.icon}</span>
                    <span className="text-[11px]">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Target Language if activeTab === 'translate' */}
          {activeTab === 'translate' && (
            <div className="space-y-1.5">
              <label className="font-bold text-slate-300">الترجمة إلى:</label>
              <div className="grid grid-cols-4 gap-1.5">
                {['الإنجليزية', 'العربية', 'الفرنسية', 'التركية', 'الإسبانية', 'الألمانية', 'الروسية', 'الصينية'].map(
                  (lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => setTargetLang(lang)}
                      className={`py-1.5 px-2 rounded-xl border text-center text-xs transition ${
                        targetLang === lang
                          ? 'border-indigo-500 bg-indigo-950/60 text-white font-bold'
                          : 'border-slate-800 bg-slate-900/50 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      {lang}
                    </button>
                  )
                )}
              </div>
            </div>
          )}

          {/* Privacy Consent for Summarize */}
          {activeTab === 'summarize' && (
            <div className="p-3.5 bg-slate-900/80 rounded-2xl border border-indigo-500/30 space-y-2.5">
              <div className="flex items-center gap-2 text-indigo-300 font-bold text-xs">
                <ShieldCheck className="w-4 h-4 text-indigo-400" />
                <span>إشعار الخصوصية الصريحة</span>
              </div>
              <p className="text-[11px] text-slate-300 leading-relaxed">
                التزاماً بسياسة الخصوصية الصارمة لمنصة وتين، لا يقرأ الذكاء الاصطناعي محادثاتك تلقائياً.
                عند تفعيل الخيار أدناه، سيتم إرسال مقتطف من آخر 10 رسائل فقط إلى وتين AI لتقديم ملخص سريع مع استخراج أهم القرارات.
              </p>
              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={summarizeConsent}
                  onChange={(e) => setSummarizeConsent(e.target.checked)}
                  className="rounded text-indigo-600 focus:ring-0 bg-slate-800 border-slate-700"
                />
                <span className="text-xs font-bold text-white">
                  أوافق على تلخيص آخر 10 رسائل في هذه المحادثة
                </span>
              </label>
            </div>
          )}

          {/* Trigger Generate Button */}
          <button
            onClick={() => handleRunAITask()}
            disabled={isLoading || (activeTab === 'summarize' && !summarizeConsent)}
            className="w-full py-2.5 rounded-2xl bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white font-black text-xs shadow-lg shadow-indigo-600/20 active:scale-[0.98] transition flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>جاري المعالجة الذكية عبر وتين AI...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                <span>
                  {activeTab === 'improve' && 'تحسين الصياغة الآن'}
                  {activeTab === 'tone' && 'تطبيق هذا الأسلوب'}
                  {activeTab === 'reply' && 'توليد اقتراحات الرد'}
                  {activeTab === 'translate' && `ترجمة إلى ${targetLang}`}
                  {activeTab === 'summarize' && 'إنشاء ملخص المحادثة'}
                </span>
              </>
            )}
          </button>

          {/* Error Message Alert */}
          {errorMessage && (
            <div className="p-3 rounded-2xl bg-rose-950/50 border border-rose-500/40 text-rose-200 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p>{errorMessage}</p>
                {errorMessage.includes('حد الاستخدام') && onOpenPremium && (
                  <button
                    onClick={onOpenPremium}
                    className="mt-1 text-[11px] font-bold text-amber-300 underline block"
                  >
                    ترقية الحساب إلى وتين Premium 👑 للوصول غير المحدود
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Result View: Text Result */}
          {resultText && (
            <div className="p-3.5 bg-[#0e1626] rounded-2xl border border-indigo-500/40 space-y-2 animate-in fade-in">
              <div className="flex items-center justify-between text-indigo-400 text-[11px] font-bold">
                <span>نتيجة وتين AI ✨:</span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleSpeak(resultText)}
                    className="p-1 rounded-lg hover:bg-slate-800 text-slate-300 hover:text-white"
                    title={isSpeaking ? 'إيقاف الصوت' : 'استمع للرد بصوت وتين'}
                  >
                    {isSpeaking ? <VolumeX className="w-3.5 h-3.5 text-indigo-400" /> : <Volume2 className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={() => handleCopyResult(resultText)}
                    className="p-1 rounded-lg hover:bg-slate-800 text-slate-300 hover:text-white"
                    title="نسخ النتيجة"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              <div className="text-slate-100 text-xs leading-relaxed whitespace-pre-wrap">
                {resultText}
              </div>

              {/* Apply Action Buttons */}
              <div className="pt-2 flex items-center gap-2">
                <button
                  onClick={() => handleApply(resultText)}
                  className="flex-1 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-1 shadow"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>وضع في خانة الإدخال</span>
                </button>
                <button
                  onClick={() => handleRunAITask()}
                  className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold"
                >
                  إعادة المحاولة
                </button>
              </div>
            </div>
          )}

          {/* Result View: Suggested Replies */}
          {suggestedReplies.length > 0 && (
            <div className="space-y-2 animate-in fade-in">
              <label className="font-bold text-indigo-300 text-xs flex items-center gap-1">
                <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
                <span>اختر الرد الأنسب لإرساله:</span>
              </label>
              <div className="space-y-1.5">
                {suggestedReplies.map((reply, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleApply(reply)}
                    className="w-full p-2.5 rounded-2xl bg-slate-900 hover:bg-indigo-950/60 border border-slate-800 hover:border-indigo-500/50 text-right text-xs text-slate-200 hover:text-white transition flex items-center justify-between group"
                  >
                    <span className="leading-relaxed">{reply}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-indigo-400 shrink-0 transform rotate-180" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Zero Data Leakage Privacy Guarantee */}
          <div className="pt-2 flex items-center justify-center gap-1.5 text-[10px] text-slate-500 text-center">
            <Lock className="w-3 h-3 text-emerald-500" />
            <span>حماية الخصوصية: لا يتم تخزين أو مشاركة أي محادثات شخصية مع جهات خارجية.</span>
          </div>
        </div>
      </div>
    </div>
  );
};
